<script>
import axios from 'axios'
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue'
import Agreement from '@components/web-checkin-agreement'
import { propertyComputed, appMethods } from '@state/helpers'
import Compressor from 'compressorjs'

export default {
  page() {
    return {
      title: `${this.property.name} - Web Checkin Details`,
      meta: [{ name: 'description', content: 'Check-in registration form' }],
    }
  },

  components: { Agreement, VueTimepicker },

  props: {
    dropdown: { type: Object, required: true },
    reservation: { type: Object, required: true },
  },

  data() {
    return {
      form: {
        arrivalTime: '',
        title: '',
        firstName: '',
        lastName: '',
        address: '',
        email: '',
        phoneNumber: '',
        purposeOfStay: '',
        identificationPhoto: null,
        signature: '',
        accompanying: [],
      },
      agreement: {
        valuablesBought: false,
        safeDeposit: false,
        costAndCharges: false,
        smoking: false,
        pet: false,
        injuries: false,
        durian: false,
        kids: false,
        drone: false,
        outsideFood: false,
      },
      purposeOfStay: [
        { text: 'Business', value: 'Business' },
        { text: 'Leisure', value: 'Leisure' },
        { text: 'Honeymoon', value: 'Honeymoon' },
        { text: 'Family', value: 'Family' },
      ],
      showChildDisclaimer: false,
      showDroneDisclaimer: false,
      showOutsideFoodDisclaimer: false,
      loading: false,
    }
  },

  computed: {
    ...propertyComputed,
  },

  mounted() {
    this.form.arrivalTime = this.reservation.arrivalTime
    this.form.title = this.reservation.title
    this.form.firstName = this.reservation.firstName
    this.form.lastName = this.reservation.lastName
    this.form.address = this.reservation.address
    this.form.email = this.reservation.email
    this.form.phoneNumber = this.reservation.phoneNumber
    this.form.purposeOfStay = this.reservation.purposeOfStay
  },

  methods: {
    ...appMethods,
    onPhotoIdChanged(e, cb) {
      const file = e.target.files[0]

      // eslint-disable-next-line
      new Compressor(file, {
        maxWidth: 1280,
        maxHeight: 960,
        success(result) {
          cb(result)
        },
        error(err) {
          cb(null)
          console.log(err)
        },
      })
    },
    getFormData() {
      let FD = new FormData()

      FD.append('bookingId', this.reservation.id)
      FD.append('bookingLastName', this.reservation.lastName)
      FD.append('bookingProperty', this.property.id)

      Object.keys(this.form).forEach((key) => {
        if (key === 'accompanying') {
          this.form[key].forEach((accompany) => {
            FD.append(`accompanying[][name]`, accompany.name)
            FD.append(
              `accompanying[][photoId]`,
              accompany.photoId,
              accompany.photoId.name
            )
          })
        } else if (key === 'identificationPhoto') {
          FD.append(key, this.form[key], this.form[key].name)
        } else {
          FD.append(key, this.form[key])
        }
      })

      return FD
    },
    checkIn() {
      this.loading = true
      axios
        .post('/api/v2/check-in', this.getFormData(), {
          headers: { 'content-type': 'multipart/form-data' },
        })
        .then((res) => {
          this.loading = false

          // push router to web-checkin page
          this.$router.push({
            name: 'web-checkin',
            params: {
              permalink: this.$route.params.permalink,
              alert:
                'Thank you for using web check-in. We look forward to welcoming you and ensuring a most enjoyable visit.',
            },
          })
        })
        .catch(() => {
          this.loading = false
          this.showAlert({
            type: 'error',
            message: 'Failed to process your web check-in. Please try again.',
          })
        })
    },
    addAccompanying() {
      this.form.accompanying.push({
        name: '',
        photoId: null,
      })
    },
    removeAccompanying(index) {
      this.$delete(this.form.accompanying, index)
    },
  },
}
</script>

<template>
  <section class="mt-6">
    <div class="container container-page mx-auto px-6">
      <BaseBox title="Registration Form" class="mb-6">
        <ValidationObserver v-slot="{ invalid, passes }" slim>
          <form @submit.prevent="passes(checkIn)">
            <div class="flex flex-wrap">
              <BaseInput
                :value="reservation.arrivalDate"
                class="sm:w-1/2 lg:w-3/12"
                label="Arrival date"
                disabled
              />
              <!-- arrival date -->

              <div class="px-3 mb-6 w-full sm:w-1/2 lg:w-3/12">
                <label class="label">Arrival time *</label>
                <VueTimepicker
                  v-model="form.arrivalTime"
                  :hour-range="[[14, 23]]"
                  input-width="100%"
                />
              </div>
              <!-- arrival time -->

              <BaseInput
                :value="reservation.departureDate"
                class="sm:w-1/2 lg:w-3/12"
                label="Departure date"
                disabled
              />
              <!-- departure date -->

              <BaseInput
                :value="reservation.departureTime"
                class="sm:w-1/2 lg:w-3/12"
                label="Departure time"
                disabled
              />
              <!-- departure time -->

              <div class="w-full lg:w-2/12">
                <BaseSelect
                  v-model="form.title"
                  class="md:w-1/3 lg:w-full"
                  label="Title *"
                  rules="required"
                  :items="dropdown.titles"
                />
                <!-- title -->
              </div>

              <BaseInput
                v-model="form.firstName"
                class="md:w-1/2 lg:w-5/12"
                label="First name *"
                rules="required|min:3"
              />
              <!-- first name -->

              <BaseInput
                v-model="form.lastName"
                class="md:w-1/2 lg:w-5/12"
                label="Last name *"
                rules="required|min:3"
              />
              <!-- last name -->

              <BaseTextarea
                v-model="form.address"
                class="md:w-1/2"
                label="Address *"
                rules="required"
              ></BaseTextarea>
              <!-- address -->

              <BaseInput
                v-model="form.email"
                class="md:w-1/2 lg:w-3/12"
                label="Email *"
                type="email"
                rules="required|email"
              />
              <!-- email -->

              <BaseInput
                v-model="form.phoneNumber"
                class="md:w-1/2 lg:w-3/12"
                label="Mobile number *"
                type="tel"
                rules="required"
              />
              <!-- phone -->

              <BaseSelect
                v-model="form.purposeOfStay"
                class="md:w-1/2 lg:w-3/12"
                label="Purpose of stay *"
                rules="required"
                :items="purposeOfStay"
              />
              <!-- purpose of stay -->

              <BaseValidationProvider
                v-slot="{ errors, validate }"
                rules="required|ext:jpg,jpeg|size:10000"
                class="px-3 mb-6 w-full md:w-1/2 lg:w-3/12"
                label="Scan/photo of passport/ID no *"
              >
                <input
                  type="file"
                  accept="image/jpeg, image/jpg"
                  @change="
                    onPhotoIdChanged($event, (res) => {
                      form.identificationPhoto = res
                    }) || validate($event)
                    "
                />
              </BaseValidationProvider>
              <!-- identification photo -->

              <h4
                v-if="form.accompanying.length"
                class="font-medium w-full p-3 mb-3"
              >
                Accompanying
              </h4>

              <div
                v-for="(accompany, index) in form.accompanying"
                :key="`accompany-${index}`"
                class="w-full border border-gray-500 bg-gray-200 flex flex-wrap relative mx-3 p-3 mb-6"
              >
                <BaseInput
                  v-model="accompany.name"
                  class="md:w-1/2"
                  label="Name"
                  rules="required"
                />

                <BaseValidationProvider
                  v-slot="{ errors, validate }"
                  rules="required|ext:jpg,jpeg|size:10000"
                  class="mb-6 md:w-1/2"
                  label="scan/photo of passport/ID no"
                >
                  <input
                    type="file"
                    accept="image/jpeg, image/jpg"
                    @change="
                      onPhotoIdChanged(
                        $event,
                        (result) => (accompany.photoId = result)
                      ) || validate($event)
                      "
                  />
                </BaseValidationProvider>

                <button
                  class="absolute top-0 right-0 p-1 md:p-2 text-red-500 focus:outline-none"
                  type="button"
                  @click="removeAccompanying(index)"
                >
                  <svg
                    class="w-6 h-6"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              <div class="px-3 mb-6 w-full">
                <BaseButton
                  v-if="form.accompanying.length < 10"
                  class="btn bg-gray-300 mb-6"
                  type="button"
                  @click="addAccompanying"
                >
                  Add accompanying guest
                </BaseButton>
              </div>

              <Agreement :policy="property.policy" />

              <BaseSignaturePad
                v-model="form.signature"
                label="Sign *"
                rules="required"
              />
              <!-- sign -->
            </div>

            <div class="px-3">
              <button
                type="submit"
                :class="[
                  'btn',
                  { 'btn-disabled': loading },
                  'btn-gold',
                ]"
              >
                <i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
                <span v-else>Check-in</span>
              </button>
            </div>
          </form>
        </ValidationObserver>
      </BaseBox>
    </div>
  </section>
</template>
